import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import {
  Info as InfoIcon,
  CheckCircle2 as CheckCircle2Icon,
  AlertTriangle as AlertTriangleIcon,
  XCircle as XCircleIcon,
  TrendingUp as TrendingUpIcon,
  DollarSign as DollarSignIcon,
  BarChart3 as BarChart3Icon,
} from 'lucide-react';
import ConfidenceBadge from './ConfidenceBadge';
import ExpertValidationBadge from './ExpertValidationBadge';

/**
 * ConfidenceIndicator - Clickable badge with popover showing confidence details
 * Pixel-perfect port from Polymet confidence-indicator.tsx
 * 
 * @param {Object} props
 * @param {Object} props.rating - Confidence rating object
 * @param {'low' | 'medium' | 'high'} props.rating.overall - Overall confidence level
 * @param {number} props.rating.score - Numeric score (0-100)
 * @param {Array} props.rating.factors - Array of confidence factors
 * @param {Array} props.rating.recommendations - Array of recommendation strings
 * @param {Object} [props.rating.expertValidation] - Expert validation data
 * @param {Function} [props.onViewDetails] - Callback when "View Full Details" is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
const ConfidenceIndicator = ({
  rating,
  onViewDetails,
  className = '',
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  if (!rating) {
    return null;
  }

  const categoryIcons = {
    revenue: TrendingUpIcon,
    expenses: DollarSignIcon,
    normalization: BarChart3Icon,
  };

  // Using inline styles to ensure colors render correctly
  const levelConfig = {
    low: {
      icon: XCircleIcon,
      styles: {
        iconColor: '#DC2626', // red-600
        textColor: '#DC2626', // red-600
        backgroundColor: '#FEF2F2', // red-50
        borderColor: '#FECACA', // red-200
        progressColor: '#EF4444', // red-500
      },
    },
    medium: {
      icon: AlertTriangleIcon,
      styles: {
        iconColor: '#D97706', // amber-600
        textColor: '#D97706', // amber-600
        backgroundColor: '#FFFBEB', // amber-50
        borderColor: '#FDE68A', // amber-200
        progressColor: '#F59E0B', // amber-500
      },
    },
    high: {
      icon: CheckCircle2Icon,
      styles: {
        iconColor: '#059669', // emerald-600
        textColor: '#059669', // emerald-600
        backgroundColor: '#ECFDF5', // emerald-50
        borderColor: '#A7F3D0', // emerald-200
        progressColor: '#10B981', // emerald-500
      },
    },
  };

  const config = levelConfig[rating.overall] || levelConfig.low;
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Main Badge/Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:shadow-sm"
          style={{
            backgroundColor: config.styles.backgroundColor,
            borderColor: config.styles.borderColor,
          }}
        >
          <Icon className="w-4 h-4" style={{ color: config.styles.iconColor }} />
          <span className="text-sm font-medium" style={{ color: config.styles.textColor }}>
            {rating.overall === 'high' && intl.formatMessage({ id: 'succession.confidence_rating.indicator.high' })}
            {rating.overall === 'medium' && intl.formatMessage({ id: 'succession.confidence_rating.indicator.medium' })}
            {rating.overall === 'low' && intl.formatMessage({ id: 'succession.confidence_rating.indicator.low' })}{' '}
            {intl.formatMessage({ id: 'succession.confidence_rating.indicator.confidence_suffix' })}
          </span>
          <InfoIcon className="w-3.5 h-3.5 text-succession-text-secondary" />
        </button>

        {/* Popover */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Popover Content */}
            <div className="absolute z-50 top-full right-0 mt-2 w-96 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-succession-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.confidence_rating.indicator.valuation_confidence_title' })}
                  </h4>
                  <ConfidenceBadge level={rating.overall} score={rating.score} showScore size="sm" />
                </div>
                
                {/* Progress Bar */}
                <div
                  className="relative h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: '#F3F4F6' }} // gray-100
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${rating.score}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: config.styles.progressColor }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {/* Factors Summary */}
                <div>
                  <h5 className="text-xs font-semibold text-succession-text-secondary uppercase mb-2">
                    {intl.formatMessage({ id: 'succession.confidence_rating.indicator.data_quality_factors_title' })}
                  </h5>
                  <div className="space-y-2">
                    {rating.factors.map((factor) => {
                      const CategoryIcon = categoryIcons[factor.category] || InfoIcon;
                      return (
                        <div
                          key={factor.id}
                          className="flex items-start gap-2 text-sm"
                        >
                          <CategoryIcon className="w-4 h-4 text-succession-text-secondary mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-succession-text-primary">{factor.title}</div>
                            <div className="text-xs text-succession-text-secondary">
                              {factor.description}
                            </div>
                          </div>
                          <ConfidenceBadge level={factor.level} size="sm" showIcon={false} />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recommendations */}
                {rating.recommendations && rating.recommendations.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-succession-text-secondary uppercase mb-2">
                      {intl.formatMessage({ id: 'succession.confidence_rating.indicator.how_to_improve_title' })}
                    </h5>
                    <div className="space-y-1.5">
                      {rating.recommendations.map((rec, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-xs text-succession-text-secondary"
                        >
                          <div
                            className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0"
                            style={{ backgroundColor: '#F59E0B' }} // amber-500
                          />
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* High Confidence Message */}
                {rating.overall === 'high' && (!rating.recommendations || rating.recommendations.length === 0) && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-lg"
                    style={{
                      backgroundColor: '#ECFDF5', // emerald-50
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: '#A7F3D0', // emerald-200
                    }}
                  >
                    <CheckCircle2Icon
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: '#059669' }} // emerald-600
                    />
                    <p className="text-xs" style={{ color: '#047857' }}> {/* emerald-700 */}
                      {intl.formatMessage({ id: 'succession.confidence_rating.indicator.excellent_confidence_message' })}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer with View Details button */}
              {onViewDetails && (
                <div className="p-3 border-t border-succession-border">
                  <button
                    type="button"
                    onClick={() => {
                      onViewDetails();
                      setIsOpen(false);
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-succession-text-primary border border-succession-border rounded-lg hover:bg-succession-bg-secondary transition-colors"
                  >
                    {intl.formatMessage({ id: 'succession.confidence_rating.indicator.view_full_details_button' })}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Expert Validation Badge - Shows independently */}
      {rating.expertValidation && rating.expertValidation.isValidated && (
        <ExpertValidationBadge validation={rating.expertValidation} size="sm" />
      )}
    </div>
  );
};

export default ConfidenceIndicator;
